import {
  DevHubBox,
  DevHubButton,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

const FIELD_STYLE = { style: { paddingRight: '2rem' } } as const;

const DEFAULT_TOKEN_URLS: Record<AuthType, string> = {
  sat: '',
  azureAd: 'https://login.microsoftonline.com/{tenant-id}/oauth2/v2.0/token',
};

const TOKEN_URL_PLACEHOLDERS: Record<AuthType, string> = {
  sat: 'https://sat.example.com/oauth/token',
  azureAd: 'https://login.microsoftonline.com/{tenant-id}/oauth2/v2.0/token',
};

export type AuthType = 'sat' | 'azureAd';

export interface EntryCredentialsState {
  authType: AuthType;
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
  scopes: string;
}

/**
 * Empty entry credentials
 */
export const EMPTY_ENTRY_CREDENTIALS: EntryCredentialsState = {
  authType: 'sat',
  tokenUrl: '',
  clientId: '',
  clientSecret: '',
  scopes: '',
};

interface EntryCredentialsFieldsProps {
  value: EntryCredentialsState;
  onChange: (next: EntryCredentialsState) => void;
  tokenUrlError?: string | null;
  caption?: string;
}

/**
 * Shared credential form fields for a2a agent and FLOW:Context entries.
 * Supports SAT and Azure AD auth types with sensible defaults per type.
 * @param options - Options for the credential fields
 * @param options.value - Current values for the credential fields
 * @param options.onChange - Callback to update the credential values
 * @param options.tokenUrlError - Optional error message for the token URL field
 * @param options.caption - Optional caption for the credential fields
 */
export function EntryCredentialsFields({
  value,
  onChange,
  tokenUrlError,
  caption,
}: EntryCredentialsFieldsProps) {
  const setField = <K extends keyof EntryCredentialsState>(
    key: K,
    val: EntryCredentialsState[K],
  ) => onChange({ ...value, [key]: val });

  const switchAuthType = (next: AuthType) => {
    onChange({
      ...value,
      authType: next,
      tokenUrl: DEFAULT_TOKEN_URLS[next],
    });
  };

  return (
    <DevHubBox display="flex" flexDirection="column" style={{ gap: 10 }}>
      {caption && (
        <DevHubTypography variant="caption" style={{ opacity: 0.6 }}>
          {caption}
        </DevHubTypography>
      )}

      {/* Auth type selector */}
      <DevHubBox display="flex" style={{ gap: 6 }}>
        <DevHubButton
          size="small"
          variant={value.authType === 'sat' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => switchAuthType('sat')}
        >
          SAT
        </DevHubButton>
        <DevHubButton
          size="small"
          variant={value.authType === 'azureAd' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => switchAuthType('azureAd')}
        >
          Azure AD
        </DevHubButton>
      </DevHubBox>

      <DevHubTextField
        label="Token URL"
        fullWidth
        value={value.tokenUrl}
        onChange={(e: any) => setField('tokenUrl', e.target.value)}
        placeholder={TOKEN_URL_PLACEHOLDERS[value.authType]}
        error={!!tokenUrlError}
        helperText={tokenUrlError ?? undefined}
        {...FIELD_STYLE}
      />
      <DevHubTextField
        label="Client ID"
        fullWidth
        value={value.clientId}
        onChange={(e: any) => setField('clientId', e.target.value)}
        {...FIELD_STYLE}
      />
      <DevHubTextField
        label="Client Secret"
        fullWidth
        type="password"
        value={value.clientSecret}
        onChange={(e: any) => setField('clientSecret', e.target.value)}
        {...FIELD_STYLE}
      />
      <DevHubTextField
        label="Scopes (comma-separated, optional)"
        fullWidth
        value={value.scopes}
        onChange={(e: any) => setField('scopes', e.target.value)}
        {...FIELD_STYLE}
      />
    </DevHubBox>
  );
}
